/** The portal destinations with no backend behind them.
 *
 * They are in the navigation because the brief asks for them and because a
 * capability nobody can see is a capability nobody can plan around. What they
 * must never do is show a number: an employee who reads "2 approvals waiting"
 * off a fabricated card goes looking for two approvals that do not exist.
 *
 * The audit behind each of these is in `docs/PORTAL-BACKEND-GAPS.md`. Neither is
 * "an endpoint is missing" — both are "the model cannot answer the question".
 * `ConfigurationItem` has an owning *team* and no owning person. That is a
 * design decision to take, not a ticket to pick up, and saying so is more use
 * than a spinner.
 *
 * Notifications used to be the other entry here, on the grounds that nothing
 * stored "notification, unread, for this contact". That was true and was
 * fixed: `core.Notification` is written wherever an outbound email is queued,
 * and `/notifications` is a working screen.
 *
 * Approvals used to be the third entry here, on the grounds that
 * `change.Approval.approver_agent` points at an Agent and an employee is not
 * one. That was true and was fixed: `tickets.RequestApproval` addresses a
 * Contact, and `/approvals` is a working screen. Kept in the history because the
 * shape of the fix — a second model rather than widening the first — is the
 * answer to the same question if `assets` is ever taken up.
 */
import { CapabilityState, type CapabilityStateProps } from '../../components/CapabilityState'

type Topic = 'assets'

const TOPICS: Record<Topic, CapabilityStateProps> = {
  assets: {
    title: 'My assets',
    description: 'The equipment and software assigned to you.',
    summary:
      'The CMDB is real and populated, but it records which team owns a device, not which person holds it — so it cannot yet answer "what do I have?"',
    available: [
      'Configuration items are live in the CMDB with type, status and criticality',
      'Assets can already be linked to a ticket, so "my laptop, this fault" works from the agent side',
    ],
    missing: [
      '`ConfigurationItem` has `owner_team` and no assigned person. Nothing in the model maps a device to a Contact',
      'A portal endpoint scoped to the signed-in employee',
    ],
    api: ['GET /api/portal/assets/', 'a `assigned_to` field on cmdb.ConfigurationItem'],
    unlocks: [
      'See your laptop, phone and licences without asking IT',
      'Raise a fault against a specific device in two clicks instead of describing it',
    ],
  },
}

export function PortalPending({ topic }: { topic: Topic }) {
  return <CapabilityState {...TOPICS[topic]} />
}
